import React, { useMemo } from 'react';

import { ModalCombinedType, ModalDialogSizes } from '../ModalDialogTypes';
import ModalDialogGrowWithContentContext, {
    useModalDialogGrowWithContentContext,
} from './ModalDialogGrowWithContentContext';
import ModalDialogNamespaceContext, { useModalDialogNamespaceContext } from './ModalDialogNamespaceContext';
import ModalDialogPortalWrapperContext, { useModalDialogPortalWrapperContext } from './ModalDialogPortalWrapperContext';
import ModalDialogSizeContext, { useModalDialogSizeContext } from './ModalDialogSizeContext';
import ModalDialogSwipeContext, { useModalDialogSwipeContext } from './ModalDialogSwipeContext';
import ModalDialogTypeContext, { useModalDialogTypeContext } from './ModalDialogTypeContext';

type ModalDialogContextProps = {
    namespace?: string;
    onCloseDownSwipe?: boolean;
    type?: ModalCombinedType;
    size?: ModalDialogSizes;
    growWithContent?: boolean;
    portalElement: HTMLDivElement | null;
} & React.PropsWithChildren;

export const useModalDialogContext = (
    namespace?: string,
    type?: ModalCombinedType,
    size?: ModalDialogSizes,
    onCloseDownSwipe?: boolean,
    growWithContent?: boolean,
) => {
    const contextNamespace = useModalDialogNamespaceContext();
    const actualNamespace = contextNamespace ?? namespace;

    const contextSize = useModalDialogSizeContext();
    const actualSize = contextSize ?? size;

    const contextSwipeDown = useModalDialogSwipeContext();
    const shouldCloseOnSwipeDown = onCloseDownSwipe ?? contextSwipeDown;

    const contextGrowWithContent = useModalDialogGrowWithContentContext();
    const shouldGrowWithContent = contextGrowWithContent ?? growWithContent;

    const contextPortalElement = useModalDialogPortalWrapperContext();

    const contextType = useModalDialogTypeContext();
    const modalType = useMemo(() => {
        if (type) {
            return type;
        }
        if (contextType === 'standard' || contextType === 'sub') {
            return 'sub';
        }

        return 'standard';
    }, [contextType, type]);

    return {
        contextNamespace: actualNamespace,
        contextSize: actualSize,
        contextType: modalType,
        contextSwipe: shouldCloseOnSwipeDown,
        contextGrowWithContent: shouldGrowWithContent,
        contextPortalElement,
    };
};

const ModalDialogContext: React.FC<ModalDialogContextProps> = ({
    namespace,
    onCloseDownSwipe,
    type,
    size,
    growWithContent,
    portalElement,
    children,
}) => (
    <ModalDialogPortalWrapperContext.Provider value={portalElement}>
        <ModalDialogNamespaceContext.Provider value={namespace}>
            <ModalDialogGrowWithContentContext.Provider value={growWithContent}>
                <ModalDialogSwipeContext.Provider value={onCloseDownSwipe}>
                    <ModalDialogTypeContext.Provider value={type}>
                        <ModalDialogSizeContext.Provider value={size}>{children}</ModalDialogSizeContext.Provider>
                    </ModalDialogTypeContext.Provider>
                </ModalDialogSwipeContext.Provider>
            </ModalDialogGrowWithContentContext.Provider>
        </ModalDialogNamespaceContext.Provider>
    </ModalDialogPortalWrapperContext.Provider>
);

export default ModalDialogContext;
